
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Flag } from "lucide-react";
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface ReportDialogProps {
  contentType: 'post' | 'comment' | 'user';
  contentId: string;
  reportedUserId: string;
  onReport: (reason: string) => void;
}

export const ReportDialog = ({ contentType, contentId, reportedUserId, onReport }: ReportDialogProps) => {
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [isSelfContent, setIsSelfContent] = useState(false);
  const { toast } = useToast();

  // Get current user ID when component mounts
  useEffect(() => {
    const fetchCurrentUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setCurrentUserId(session.user.id);
        setIsSelfContent(session.user.id === reportedUserId);
      }
    };
    
    fetchCurrentUser();
  }, [reportedUserId]);

  // Reset error when dialog opens/closes
  useEffect(() => {
    setError(null);
  }, [isOpen]);

  // Validate the content ID when dialog opens
  useEffect(() => {
    if (isOpen && contentId) {
      validateContent();
    }
  }, [isOpen, contentId]);

  // Check authentication status when dialog opens
  useEffect(() => {
    if (isOpen) {
      checkAuth();
    }
  }, [isOpen]);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      toast({
        title: "Please sign in",
        description: "You need to be signed in to report content.",
        variant: "destructive",
      });
      setIsOpen(false);
    }
  };

  const validateContent = async () => {
    try {
      if (contentType === 'post') {
        const { data, error } = await supabase
          .from('posts')
          .select('id')
          .eq('id', contentId)
          .maybeSingle();
          
        if (error || !data) {
          setError("The post you're trying to report doesn't exist or has been removed.");
        }
      } else if (contentType === 'comment') {
        const { data, error } = await supabase
          .from('comments')
          .select('id')
          .eq('id', contentId)
          .maybeSingle();
          
        if (error || !data) {
          setError("The comment you're trying to report doesn't exist or has been removed.");
        }
      }
    } catch (error) {
      console.error('Error validating content:', error);
      setError("There was an error validating the content. Please try again later.");
    }
  };

  const handleSubmit = async () => {
    if (!reason.trim()) {
      toast({
        title: "Please provide a reason",
        description: "We need to understand why you're reporting this content.",
        variant: "destructive",
      });
      return;
    }

    if (error) {
      toast({
        title: "Cannot report this content",
        description: error,
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        toast({
          title: "Please sign in",
          description: "You need to be signed in to report content.",
          variant: "destructive",
        });
        setIsOpen(false);
        return;
      }

      // Insert the report
      const { error: insertError } = await supabase
        .from('reports')
        .insert({
          content_type: contentType,
          content_id: contentId,
          reporter_id: session.user.id,
          reported_user_id: reportedUserId,
          reason: reason.trim(),
          report_type: 'hate_speech', // Using default value as defined in the schema
        });

      if (insertError) throw insertError;
      
      toast({
        title: "Report submitted",
        description: "Thank you for helping keep our community positive. We'll review this content shortly.",
      });

      // Only try to get the report count after successfully submitting the report
      const { data: reportCount, error: countError } = await supabase
        .from('content_report_counts')
        .select('report_count')
        .eq('content_type', contentType)
        .eq('content_id', contentId)
        .maybeSingle();
        
      if (!countError && reportCount && reportCount.report_count >= 2) {
        // Trigger the hide action if threshold is met
        onReport(reason);
      }

      setReason("");
      setIsOpen(false);
    } catch (error) {
      console.error('Error submitting report:', error);
      const errorMessage = error instanceof Error 
        ? error.message 
        : "An error occurred while submitting your report. Please try again later.";
      
      setError(errorMessage);
      
      toast({
        title: "Error submitting report",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Don't render the report button if it's the user's own content
  if (isSelfContent) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600 hover:bg-red-50 space-x-3">
          <Flag className="w-4 h-4" />
          <span>Report</span>
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Report {contentType}</DialogTitle>
          <DialogDescription>
            Report inappropriate content to help keep our community positive
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            This community is focused on positivity and support. Help us maintain this environment by reporting content that:
          </p>
          <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
            <li>Contains hate speech or harassment</li>
            <li>Promotes negativity or harmful behavior</li>
            <li>Is intentionally disruptive to the community</li>
          </ul>
          <Textarea
            placeholder="Please explain why you're reporting this content..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="min-h-[100px]"
          />
          {error && (
            <p className="text-sm text-red-500 mt-2">{error}</p>
          )}
          <Button 
            onClick={handleSubmit} 
            className="w-full bg-red-500 hover:bg-red-600"
            disabled={isSubmitting || !!error}
          >
            {isSubmitting ? "Submitting..." : "Submit Report"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
